import { useMemo, useCallback, useState } from "react";
import { FlatList, Pressable } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { CameraListItem, useCamerasListState } from "@src/features/cameras";
import { getCameraKeyString } from "@src/utils/key-parser";
import { sizes } from "@src/constants/sizes";
import type { SortState } from "@src/features/clips/types/camera";
import { SearchAndSortBar } from "./SearchAndSortBar.web";
import { useResponsiveGridColumns } from "@src/hooks/useResponsiveGridColumns";
import { track } from "@visioni/a3s-analytics";
import { CameraClicked, cameraClickedProps } from "@src/features/cameras/analytics";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import {
  useCameraCollection,
  buildGroupedGridItems,
  type CameraGridItem,
} from "@src/features/cameras/hooks/useCameraCollection";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSnapshots } from "@visioni/a3s-media/app";

// Cap on columns however wide the viewport gets: past four the snapshots are
// small enough that scanning a wall of them is harder than scrolling.
const MAX_COLUMNS = 4;

// FlatList's numColumns can't hold a full-width site header, so the grid is
// laid out as a flat list of headers and pre-chunked rows instead. The border
// around a site is drawn piecewise across its items (sides on every item, top
// on the header, bottom on the last) so long sites stay virtualised.
type GridListItem =
  | {
      type: "header";
      siteId: string;
      siteName: string;
      siteSubtitle?: string;
      expanded: boolean;
      isLastInSite: boolean;
      key: string;
    }
  | { type: "row"; cells: CameraGridItem[]; framed: boolean; isLastInSite: boolean; key: string };

interface CameraGridProps {
  siteId?: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortState;
  onSortChange: (sortBy: SortState) => void;
}

export function CameraGrid({ siteId, searchQuery, onSearchChange, sortBy, onSortChange }: CameraGridProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const listState = useCamerasListState(siteId);
  const { data: snapshots } = useSnapshots({ refetchInterval: 30000 });
  const siteNames = useSiteNames();
  const [collapsedSiteIds, setCollapsedSiteIds] = useState<ReadonlySet<string>>(new Set());

  // A new search re-expands every site, or matches inside a collapsed site
  // would sit hidden under its header and look like no results. Reset during
  // render rather than in an effect so the hidden state never paints.
  const [collapsedForQuery, setCollapsedForQuery] = useState(searchQuery);
  if (collapsedForQuery !== searchQuery) {
    setCollapsedForQuery(searchQuery);
    setCollapsedSiteIds(new Set());
  }

  const toggleSite = useCallback((siteId: string) => {
    setCollapsedSiteIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(siteId)) next.add(siteId);
      return next;
    });
  }, []);

  const gridSpacing = theme.spacing["6xl"];
  const {
    numColumns,
    isMeasured,
    onLayout: onGridLayout,
  } = useResponsiveGridColumns(sizes.cameraCardMinWidth, MAX_COLUMNS, gridSpacing);

  const allCameras = listState.status === "success" ? listState.cameras : undefined;

  const siteOptions = useMemo(
    () => groupCamerasBySite(allCameras ?? [], "site", siteNames).map(({ siteId, siteName }) => ({ siteId, siteName })),
    [allCameras, siteNames],
  );
  // The site filter lives in the URL (`?sites=S2-1,S2-2`) so a filtered view
  // can be shared. Ids that aren't among this tenant's sites are ignored, and
  // an empty selection means every site.
  const { sites: sitesParam } = useLocalSearchParams<{ sites?: string | string[] }>();
  const selectedSiteIds = useMemo(() => {
    const raw = typeof sitesParam === "string" ? sitesParam : "";
    const requested = new Set(raw.split(",").map((id) => id.trim()));
    return siteOptions.filter((site) => requested.has(site.siteId)).map((site) => site.siteId);
  }, [sitesParam, siteOptions]);
  const setSelectedSiteIds = useCallback(
    (siteIds: string[]) => {
      router.setParams({ sites: siteIds.length > 0 ? siteIds.join(",") : undefined });
    },
    [router],
  );

  const camerasForSite = useMemo(() => {
    if (selectedSiteIds.length === 0) return allCameras;
    const selected = new Set(selectedSiteIds);
    return allCameras?.filter((camera) => selected.has(camera.site.id));
  }, [allCameras, selectedSiteIds]);

  const { groupedCameras } = useCameraCollection({
    cameras: camerasForSite,
    searchQuery,
    sortBy,
    siteNames,
  });
  const showTitle = !!siteId;

  // Keyed off the tenant's sites, not the filtered groups, so the site boxes
  // don't vanish when the filter narrows the list to one site.
  const showSiteHeaders = siteOptions.length > 1;

  const listItems = useMemo((): GridListItem[] => {
    return groupedCameras.flatMap((group) => {
      const expanded = !showSiteHeaders || !collapsedSiteIds.has(group.siteId);
      const header: GridListItem[] =
        showSiteHeaders ?
          [
            {
              type: "header",
              siteId: group.siteId,
              siteName: group.siteName,
              siteSubtitle: group.siteSubtitle,
              expanded,
              isLastInSite: !expanded,
              key: `header-${group.siteId}`,
            },
          ]
        : [];
      if (!expanded) return header;
      const gridItems = buildGroupedGridItems(group.cameras, numColumns, snapshots);
      const rows: GridListItem[] = [];
      for (let i = 0; i < gridItems.length; i += numColumns) {
        rows.push({
          type: "row",
          cells: gridItems.slice(i, i + numColumns),
          framed: showSiteHeaders,
          isLastInSite: i + numColumns >= gridItems.length,
          key: `row-${group.siteId}-${i}`,
        });
      }
      return [...header, ...rows];
    });
  }, [groupedCameras, showSiteHeaders, numColumns, snapshots, collapsedSiteIds]);

  const handleCameraSelect = useCallback(
    (camera: CameraApiResponse) => {
      const cameraKey = getCameraKeyString(camera);
      // Use the camera's actual site ID from the camera data, not the prop
      const cameraSiteId = camera.site.id;
      const cameraTenantId = camera.site.tenant;

      track(CameraClicked, cameraClickedProps(camera));

      router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}` as never);
    },
    [router],
  );

  const renderCameraCell = useCallback(
    (cell: CameraGridItem, index: number) => {
      const cellStyle = {
        width: `${100 / numColumns}%` as const,
        paddingHorizontal: gridSpacing / 2,
        paddingVertical: gridSpacing / 2,
      };
      if (!cell) {
        return <View key={`empty-${index}`} style={cellStyle} />;
      }
      const { camera, snapshot } = cell;
      return (
        <View key={getCameraKeyString(camera)} style={cellStyle}>
          <CameraListItem
            camera={camera}
            siteId={camera.site.id}
            onSelect={() => handleCameraSelect(camera)}
            href={
              `/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(getCameraKeyString(camera))}` as never
            }
            snapshot={snapshot}
          />
        </View>
      );
    },
    [gridSpacing, handleCameraSelect, numColumns],
  );

  const siteFrameStyle = useCallback(
    (isFirst: boolean, isLast: boolean) => {
      // Border and radius match the a3s-ui outlined TableContainer.
      const radius = theme.spacing.xl;
      return {
        marginBottom: isLast ? gridSpacing : 0,
        backgroundColor: theme.colors.cardGroup.background,
        borderColor: theme.colors.borderCard,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderTopWidth: isFirst ? 1 : 0,
        borderBottomWidth: isLast ? 1 : 0,
        borderTopLeftRadius: isFirst ? radius : 0,
        borderTopRightRadius: isFirst ? radius : 0,
        borderBottomLeftRadius: isLast ? radius : 0,
        borderBottomRightRadius: isLast ? radius : 0,
      };
    },
    [gridSpacing, theme],
  );

  const renderListItem = useCallback(
    ({ item }: { item: GridListItem }) => {
      if (item.type === "header") {
        // Site name, muted site id and a trailing chevron; pressing the row
        // collapses the site.
        return (
          <Pressable
            onPress={() => toggleSite(item.siteId)}
            accessibilityRole="button"
            accessibilityState={{ expanded: item.expanded }}
            style={{
              ...siteFrameStyle(true, item.isLastInSite),
              backgroundColor: theme.colors.cardGroup.headBackground,
              overflow: "hidden",
              // Separates the header from the site's cameras; a collapsed site
              // already has the frame's bottom edge here.
              ...(item.expanded && { borderBottomWidth: 1 }),
              flexDirection: "row",
              alignItems: "center",
              gap: theme.spacing.sm,
              paddingHorizontal: gridSpacing / 2,
              paddingVertical: theme.spacing.lg,
            }}
          >
            {({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => (
              <>
                {/* The table's row hover/pressed colours are translucent, so
                    they're layered over the head background, not swapped in. */}
                {(hovered || pressed) && (
                  <View
                    pointerEvents="none"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      backgroundColor: pressed ? theme.colors.table.rowPressed : theme.colors.table.rowHover,
                    }}
                  />
                )}
                <View flex={1} style={{ minWidth: 0 }}>
                  <Text textStyle="md/semibold" color="text" numberOfLines={1}>
                    {item.siteName}
                  </Text>
                  {item.siteSubtitle && (
                    <Text textStyle="sm/regular" color="textMuted" numberOfLines={1}>
                      Site: {item.siteSubtitle}
                    </Text>
                  )}
                </View>
                <Icon name={item.expanded ? "chevronDown" : "chevronRight"} size={24} color={theme.colors.icon} />
              </>
            )}
          </Pressable>
        );
      }
      // Pad short rows so cells keep their column width.
      const cells = [...item.cells, ...Array<CameraGridItem>(numColumns - item.cells.length).fill(undefined)];
      return (
        <View flexDirection="row" style={item.framed ? siteFrameStyle(false, item.isLastInSite) : undefined}>
          {cells.map(renderCameraCell)}
        </View>
      );
    },
    [gridSpacing, numColumns, renderCameraCell, siteFrameStyle, theme, toggleSite],
  );

  if (listState.status === "pending") {
    return (
      <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
        <View width="100%" gap={showTitle ? "6xl" : undefined} pb="6xl" style={{ alignSelf: "center", zIndex: 10 }}>
          {showTitle && (
            <Text textStyle="lg/bold" color="text">
              {siteId} Cameras
            </Text>
          )}
          <SearchAndSortBar
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            sortBy={sortBy}
            onSortChange={onSortChange}
            isLoading
          />
        </View>
      </View>
    );
  }

  if (listState.status === "error") {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <Text textStyle="md/regular" color="textMuted">
          Failed to load cameras
        </Text>
      </View>
    );
  }

  return (
    <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
      {/* Fixed Header Section - Title and Search/Sort Bar */}
      <View width="100%" gap={showTitle ? "6xl" : undefined} pb="6xl" style={{ alignSelf: "center", zIndex: 10 }}>
        {showTitle && (
          <Text textStyle="lg/bold" color="text">
            {siteId} Cameras
          </Text>
        )}

        {/* Search and Sort Bar - Outside scroll container */}
        <SearchAndSortBar
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          sortBy={sortBy}
          onSortChange={onSortChange}
          sites={siteOptions}
          selectedSiteIds={selectedSiteIds}
          onSiteChange={setSelectedSiteIds}
        />
      </View>

      {/* Scrollable Camera Grid */}
      <View flex={1} alignItems="center" onLayout={onGridLayout}>
        {!isMeasured ?
          null
        : listItems.length === 0 ?
          <View flex={1} justifyContent="center" alignItems="center" py="13xl">
            <Text textStyle="md/regular" color="textMuted">
              {searchQuery ? "No cameras found" : "No cameras available"}
            </Text>
          </View>
        : <FlatList
            key={numColumns}
            data={listItems}
            renderItem={renderListItem}
            // Rows don't re-render on a theme switch unless told to.
            extraData={theme}
            keyExtractor={(item) => item.key}
            showsVerticalScrollIndicator={false}
            windowSize={5}
            initialNumToRender={6}
            removeClippedSubviews
            style={{ width: "100%" }}
            contentContainerStyle={{
              // Site boxes sit flush with the search bar; ungrouped cards keep
              // the outer gutter instead.
              paddingHorizontal: showSiteHeaders ? 0 : gridSpacing / 2,
              paddingBottom: theme.spacing["8xl"],
            }}
          />
        }
      </View>
    </View>
  );
}
